import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Text,
  Title,
  Stack,
  Box,
  Button,
  NumberInput,
  Group,
  useMantineColorScheme,
  Paper,
  Badge,
  SimpleGrid,
  Divider,
  Card,
  Accordion,
  Chip,
  Avatar,
  ActionIcon,
  Grid,
  Loader,
  Center,
  Modal,
} from '@mantine/core';
import {
  IconStethoscope,
  IconHeartbeat,
  IconCut,
  IconBabyCarriage,
  IconShieldCheck,
  IconVaccine,
  IconChevronDown,
  IconArrowLeft,
} from '@tabler/icons-react';
import { authService } from '../services/authService';
import { examenService } from '../services/examenService';
import { especialidadService, Especialidad } from '../services/especialidadService';
import { temaService, Tema } from '../services/temaService';
import { ExamenEnProgreso } from '../types/examen.types';
import RecuperarExamenModal from '../components/RecuperarExamenModal';
import { examenStorage } from '../utils/examenStorage';

const ExamenFiltrosPage: React.FC = () => {
  const navigate = useNavigate();
  const { colorScheme } = useMantineColorScheme();
  const [user] = useState(authService.getCurrentUserFromStorage());

  // Estado de especialidades, temas y configuración
  const [especialidades, setEspecialidades] = useState<Especialidad[]>([]);
  const [temas, setTemas] = useState<Tema[]>([]);
  const [cargandoTemas, setCargandoTemas] = useState(true);
  const [selectedTemas, setSelectedTemas] = useState<number[]>([]); // IDs de los temas seleccionados
  const [numReactivos, setNumReactivos] = useState<number | undefined>(undefined);
  const [numCasos, setNumCasos] = useState<number | undefined>(undefined);
  const [selectedDificultad, setSelectedDificultad] = useState<number | null>(null); // 1=FÁCIL, 2=INTERMEDIO, 3=DIFÍCIL
  const [accordionValue, setAccordionValue] = useState<string[]>([]);

  // Estado para recuperación de examen
  const [examenEnProgreso, setExamenEnProgreso] = useState<ExamenEnProgreso | null>(null);
  const [mostrarModalRecuperar, setMostrarModalRecuperar] = useState(false);
  const [cargandoRecuperacion, setCargandoRecuperacion] = useState(false);

  // Estado para modal de confirmación
  const [modalConfirmacionAbierto, setModalConfirmacionAbierto] = useState(false);
  const [examenDataPendiente, setExamenDataPendiente] = useState<any>(null);

  // Modal para otro examen en progreso
  const [mostrarModalOtroExamen, setMostrarModalOtroExamen] = useState(false);
  const [otroExamen, setOtroExamen] = useState<ExamenEnProgreso | null>(null);

  // Verificar acceso premium al cargar la página
  useEffect(() => {
    const verificarAcceso = async () => {
      try {
        const { userSubscriptionService } = await import('../services/userSubscriptionService');
        const statusData = await userSubscriptionService.getSubscriptionStatus();
        
        // Usuario es gratuito si no tiene plan o si el plan tiene precio 0
        const esPlanGratuito = statusData?.isFreeplan === true || !statusData?.subscriptionId;
        
        if (esPlanGratuito) {
          // Redirigir a la página de mejorar plan si es usuario gratuito
          navigate('/mejorarplan', { replace: true });
        }
      } catch (error) {
        console.error('Error verificando acceso:', error);
        // En caso de error, redirigir al simulador principal por seguridad
        navigate('/estudiante/simulador', { replace: true });
      }
    };

    verificarAcceso();
  }, [navigate]);

  // Cargar especialidades y temas desde el backend
  useEffect(() => {
    const cargarDatos = async () => {
      try {
        setCargandoTemas(true);
        const [especialidadesData, temasData] = await Promise.all([
          especialidadService.listar(),
          temaService.listar()
        ]);
        setEspecialidades(especialidadesData);
        setTemas(temasData);
      } catch (error) {
        console.error('Error cargando datos:', error);
        alert('Error al cargar especialidades y temas');
      } finally {
        setCargandoTemas(false);
      }
    };

    cargarDatos();
  }, []);

  // Verificar examen en progreso al cargar
  useEffect(() => {
    verificarExamenEnProgreso();
  }, []);

  const verificarExamenEnProgreso = async () => {
    if (!user?.id) {
      return;
    }

    try {
      // Verificar solo exámenes FILTRADO en progreso (permitiendo otros tipos simultáneamente)
      const examen = await examenService.verificarExamenEnProgresoPorTipo(user.id, 'FILTRADO');
      if (examen) {
        setExamenEnProgreso(examen);
        setMostrarModalRecuperar(true);
      }
    } catch (error) {
      console.error('Error verificando examen en progreso:', error);
    }
  };

  const recuperarExamen = async () => {
    if (!examenEnProgreso) return;

    setCargandoRecuperacion(true);
    try {
      const progreso = await examenService.recuperarProgreso(examenEnProgreso.intentoId);

      // Navegar a la página de examen filtrado con los datos recuperados
      navigate(`/estudiante/examen-filtrado/${examenEnProgreso.intentoId}`, {
        state: { 
          examenData: progreso.examenData,
          recuperando: true,
          progreso: {
            preguntaActual: progreso.preguntaActual,
            tiempoTranscurrido: progreso.tiempoTranscurrido,
            pausado: progreso.pausado,
            respuestas: progreso.respuestas
          }
        }
      });
    } catch (error) {
      console.error('Error recuperando examen:', error);
      alert('Error al recuperar el examen');
    } finally {
      setCargandoRecuperacion(false);
    }
  };

  const comenzarNuevoExamen = async () => {
    if (!examenEnProgreso) return;

    setCargandoRecuperacion(true);
    try {
      // Abandonar el examen anterior
      await examenService.abandonarExamen(examenEnProgreso.intentoId);
      examenStorage.limpiarProgreso();

      setMostrarModalRecuperar(false);
      setExamenEnProgreso(null);
    } catch (error) {
      console.error('Error abandonando examen:', error);
      alert('Error al abandonar el examen anterior');
    } finally {
      setCargandoRecuperacion(false);
    }
  };

  // Mapeo de especialidadId a clave de categoría UI
  const mapeoEspecialidadACategoria: Record<number, string> = {
    1: 'pediatria',
    2: 'medicina',
    3: 'cirugia',
    4: 'gineco',
    5: 'urgencias'
  };

  // Función para obtener temas por especialidad
  const getTemasPorEspecialidad = (categoriaKey: string): Tema[] => {
    // Encontrar el ID de la especialidad basándose en la categoría
    const especialidadId = Object.entries(mapeoEspecialidadACategoria)
      .find(([, cat]) => cat === categoriaKey)?.[0];

    if (!especialidadId) return [];

    return temas.filter(tema => tema.especialidadId === parseInt(especialidadId));
  };

  // Dificultades hardcoded
  const dificultades = {
    1: { id: 1, nombre: 'FACIL' },
    2: { id: 2, nombre: 'INTERMEDIO' },
    3: { id: 3, nombre: 'DIFICIL' }
  };

  // Configuración de categorías con iconos y metadatos (solo las 5 categorías reales)
  const categorias = {
    pediatria: {
      nombre: "PEDIATRIA",
      icon: IconBabyCarriage,
      color: "#ffb3ba",
      descripcion: "Cuidado integral infantil"
    },
    medicina: {
      nombre: "MEDICINA INTERNA",
      icon: IconStethoscope,
      color: "#bae1ff",
      descripcion: "Especialidades médicas generales"
    },
    cirugia: {
      nombre: "CIRUGIA GENERAL",
      icon: IconCut,
      color: "#ffdfba",
      descripcion: "Procedimientos y cirugías"
    },
    gineco: {
      nombre: "GINECOLOGIA Y OBSTETRICIA",
      icon: IconHeartbeat,
      color: "#ffffba",
      descripcion: "Salud femenina y reproductiva"
    },
    urgencias: {
      nombre: "URGENCIAS",
      icon: IconShieldCheck,
      color: "#ffb3ff",
      descripcion: "Atención médica inmediata"
    }
  };

  const handleGenerarExamen = async () => {
    try {
      if (!user?.id) {
        alert("Usuario no autenticado");
        return;
      }
      if (selectedTemas.length === 0) {
        alert("Selecciona al menos un tema");
        return;
      }

      const reactivos = numReactivos || 0;
      const casos = numCasos || 0;
      
      if (reactivos === 0 && casos === 0) {
        alert("Debes solicitar al menos reactivos o casos de estudio");
        return;
      }

      // Construir objeto de dificultad si está seleccionada
      const dificultadObj = selectedDificultad ? dificultades[selectedDificultad as keyof typeof dificultades] : undefined;

      // Mapear selectedTemas (IDs de temas) a especialidad IDs
      const especialidadIdsSet = new Set<number>();
      selectedTemas.forEach(temaId => {
        const tema = temas.find(t => t.id === temaId);
        if (tema) {
          especialidadIdsSet.add(tema.especialidadId);
        }
      });
      const especialidadIds = Array.from(especialidadIdsSet);

      // Usar nueva API con examenService.crearExamenFiltrado
      const examenData = await examenService.crearExamenFiltrado(
        user.id,
        especialidadIds,
        reactivos,
        casos > 0 ? casos : undefined,
        dificultadObj,
        selectedTemas.length > 0 ? selectedTemas : undefined  // Pasar temaIds si hay temas seleccionados
      );

      if (!examenData || !examenData.intentoId) {
        alert("No se pudo generar el examen. Verifica que existan reactivos en las especialidades seleccionadas.");
        return;
      }

      // Guardar datos del examen y abrir modal de confirmación
      setExamenDataPendiente(examenData);
      setModalConfirmacionAbierto(true);

    } catch (error: any) {
      console.error("Error generando examen:", error);
      alert("Ocurrió un error al generar el examen.");
    }
  };

  const confirmarIniciarExamen = () => {
    if (examenDataPendiente) {
      setModalConfirmacionAbierto(false);
      navigate(`/estudiante/examen-filtrado/${examenDataPendiente.intentoId}`, {
        state: { examenData: examenDataPendiente }
      });
    }
  };

  return (
    <>
      <RecuperarExamenModal
        examen={examenEnProgreso}
        opened={mostrarModalRecuperar}
        onContinuar={recuperarExamen}
        onNuevo={comenzarNuevoExamen}
        loading={cargandoRecuperacion}
      />

      {/* Modal: otro examen en progreso (de otro tipo) */}
      <Modal
        opened={mostrarModalOtroExamen}
        onClose={() => setMostrarModalOtroExamen(false)}
        centered
        size="sm"
        radius="lg"
        withCloseButton={false}
        overlayProps={{ blur: 3 }}
        styles={{ content: { borderRadius: '16px' } }}
      >
        <Stack gap="lg">
          <Box>
            <Title order={4} mb="xs">Tienes un examen en progreso</Title>
            <Text size="sm" c="dimmed">
              {otroExamen?.nombreExamen}. ¿Deseas ir a ese examen?
            </Text>
          </Box>
          <Group justify="flex-end" gap="sm">
            <Button 
              variant="default" 
              radius="md" 
              onClick={() => setMostrarModalOtroExamen(false)}
            >
              Quedarme aquí
            </Button>
            <Button
              variant="filled"
              color="dark"
              radius="md"
              onClick={() => {
                if (!otroExamen) return;
                if (otroExamen.tipoExamen === 'RAPIDO') {
                  navigate('/estudiante/simulador/rapido');
                } else if (otroExamen.tipoExamen === 'ENARM') {
                  navigate('/estudiante/simulador/completo');
                }
              }}
            >
              Ir a ese examen
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Modal de confirmación antes de iniciar */}
      <Modal
        opened={modalConfirmacionAbierto}
        onClose={() => setModalConfirmacionAbierto(false)}
        centered
        size="sm"
        radius="lg"
        withCloseButton={false}
        overlayProps={{ blur: 3 }}
        styles={{
          content: {
            borderRadius: '16px',
          },
        }}
      >
        <Stack gap="lg">
          <Box>
            <Title order={4} mb="xs">Iniciar Examen Personalizado</Title>
            {examenDataPendiente && (
              <Stack gap="sm">
                <Text size="sm" c="dimmed">
                  Tu examen está listo con las siguientes características:
                </Text>
                <Stack gap={4}>
                  <Group justify="space-between">
                    <Text size="xs">Total de preguntas:</Text>
                    <Text size="xs" fw={600}>{examenDataPendiente.totalPreguntas}</Text>
                  </Group>
                  <Group justify="space-between">
                    <Text size="xs">Tiempo límite:</Text>
                    <Text size="xs" fw={600}>{examenDataPendiente.tiempoLimiteMin} min</Text>
                  </Group>
                </Stack>
                <Text size="xs" c="dimmed" mt="xs">
                  ¿Deseas comenzar el examen ahora?
                </Text>
              </Stack>
            )}
          </Box>
          <Group justify="flex-end" gap="sm">
            <Button
              variant="default"
              onClick={() => {
                setModalConfirmacionAbierto(false);
                setExamenDataPendiente(null);
              }}
              radius="md"
            >
              Cancelar
            </Button>
            <Button
              variant="filled"
              color="dark"
              onClick={confirmarIniciarExamen}
              radius="md"
            >
              Comenzar
            </Button>
          </Group>
        </Stack>
      </Modal>
      
      <Box
        style={{
          padding: '16px 24px',
          maxWidth: '1100px',
          margin: '0 auto',
          width: '100%',
        }}
      >
      {/* Page Header */}
      <Group justify="space-between" align="center" mb="sm">
        <Box style={{ textAlign: 'left' }}>
          <Title order={4} fw={600} mb={2} style={{ textAlign: 'left' }}>
            Arma tu Examen
          </Title>
          <Text size="xs" c="dimmed" style={{ textAlign: 'left' }}>
            Configura especialidades y preguntas
          </Text>
        </Box>

        <ActionIcon variant="default" size="lg" radius="md" onClick={() => navigate('/estudiante/simulador')}>
          <IconArrowLeft size={18} />
        </ActionIcon>
      </Group>

      {/* Main Content Grid */}
      <Grid align="stretch">
        {/* Left Column - Especialidades */}
        <Grid.Col span={6}>
          <Stack gap="xs" style={{ height: '100%' }}>
            {/* Accordion de Especialidades */}
            {cargandoTemas ? (
              <Center p="xl">
                <Stack align="center" gap="sm">
                  <Loader size="lg" />
                  <Text size="sm" c="dimmed">Cargando temas...</Text>
                </Stack>
              </Center>
            ) : (
            <Card
              padding="sm"
              style={{
                backgroundColor: colorScheme === 'dark'
                  ? 'rgba(30, 41, 59, 0.6)'
                  : '#fafafa',
                border: `1px solid ${colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(221, 216, 209, 0.8)'}`,
                borderRadius: '12px',
                flex: 1,
              }}
            >
              <Text size="sm" fw={600} mb="xs">Especialidades</Text>
              <Accordion
                multiple
                value={accordionValue}
                onChange={setAccordionValue}
                chevron={<IconChevronDown size={16} />}
                styles={{
                  root: {
                    backgroundColor: 'transparent',
                  },
                  chevron: {
                    color: colorScheme === 'dark' ? '#94a3b8' : '#5a5550',
                    transition: 'transform 200ms ease',
                  },
                  control: {
                    backgroundColor: 'transparent',
                    border: 'none',
                    padding: '8px 12px',
                    '&:hover': {
                      backgroundColor: colorScheme === 'dark'
                        ? 'rgba(255, 255, 255, 0.05)'
                        : 'rgba(160, 142, 115, 0.1)',
                    },
                  },
                  panel: {
                    padding: '0 12px 12px 12px',
                  },
                  item: {
                    border: 'none',
                    borderBottom: `1px solid ${colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(221, 216, 209, 0.5)'}`,
                    '&:last-child': {
                      borderBottom: 'none',
                    },
                  },
                }}
              >
                {Object.entries(categorias).map(([categoriaKey, categoria]) => {
                const temasCat = getTemasPorEspecialidad(categoriaKey);
                if (temasCat.length === 0) return null;

                const Icon = categoria.icon;
                const selectedCount = temasCat.filter(tema =>
                  selectedTemas.includes(tema.id)
                ).length;
                
                // Calcular el total de reactivos en esta especialidad
                const totalReactivosEspecialidad = temasCat.reduce((sum, tema) => 
                  sum + (tema.reactivosCount || 0), 0
                );

                return (
                  <Accordion.Item key={categoriaKey} value={categoriaKey}>
                    <Accordion.Control>
                      <Group justify="space-between" wrap="nowrap" align="center" style={{ width: '100%' }}>
                        <Group gap="sm" wrap="nowrap">
                          <Avatar
                            size="sm"
                            style={{
                              backgroundColor: colorScheme === 'dark'
                                ? 'rgba(255, 255, 255, 0.1)'
                                : categoria.color + '40',
                              color: colorScheme === 'dark' ? '#e2e8f0' : '#2d2a26',
                            }}
                          >
                            <Icon size={16} />
                          </Avatar>
                          <Box style={{ flex: 1 }}>
                            <Group gap="xs" align="center">
                              <Text
                                size="sm"
                                fw={600}
                                style={{
                                  color: colorScheme === 'dark' ? '#e2e8f0' : '#2d2a26',
                                  fontFamily: 'Inter, sans-serif',
                                  letterSpacing: '0.5px',
                                }}
                              >
                                {categoria.nombre}
                              </Text>
                              <Text
                                size="9px"
                                style={{
                                  color: colorScheme === 'dark' ? '#64748b' : '#94a3b8',
                                  fontFamily: 'Inter, sans-serif',
                                  fontWeight: 500,
                                }}
                              >
                                ({totalReactivosEspecialidad})
                              </Text>
                            </Group>
                            <Text
                              size="xs"
                              style={{
                                color: colorScheme === 'dark' ? '#94a3b8' : '#5a5550',
                                fontFamily: 'Inter, sans-serif',
                              }}
                            >
                              {categoria.descripcion}
                            </Text>
                          </Box>
                        </Group>
                        <Badge
                          size="sm"
                          variant={selectedCount > 0 ? "filled" : "outline"}
                          style={{
                            backgroundColor: selectedCount > 0
                              ? colorScheme === 'dark' ? '#0ea5e9' : '#2563eb'
                              : 'transparent',
                            borderColor: colorScheme === 'dark' ? '#0ea5e9' : '#2563eb',
                            color: selectedCount > 0
                              ? '#ffffff'
                              : colorScheme === 'dark' ? '#94a3b8' : '#5a5550',
                          }}
                        >
                          {selectedCount}/{temasCat.length}
                        </Badge>
                      </Group>
                    </Accordion.Control>

                    <Accordion.Panel>
                      <Group gap="xs">
                        {temasCat.map((tema) => {
                          const isSelected = selectedTemas.includes(tema.id);
                          return (
                            <Button
                              key={tema.id}
                              onClick={() => {
                                if (isSelected) {
                                  setSelectedTemas(prev => prev.filter(id => id !== tema.id));
                                } else {
                                  setSelectedTemas(prev => [...prev, tema.id]);
                                }
                              }}
                              size="xs"
                              variant={isSelected ? "filled" : "outline"}
                              style={{
                                backgroundColor: isSelected
                                  ? colorScheme === 'dark' ? '#0ea5e9' : '#2563eb'
                                  : 'transparent',
                                borderColor: isSelected
                                  ? colorScheme === 'dark' ? '#0ea5e9' : '#2563eb'
                                  : colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(148, 163, 184, 0.4)',
                                color: isSelected
                                  ? '#ffffff'
                                  : colorScheme === 'dark' ? '#94a3b8' : '#64748b',
                                fontSize: '11px',
                                fontWeight: isSelected ? 600 : 500,
                                fontFamily: 'Inter, sans-serif',
                                borderRadius: '16px',
                                padding: '6px 12px',
                                minHeight: 'auto',
                                height: 'auto',
                              }}
                            >
                              {tema.nombre}
                              {tema.reactivosCount !== undefined && (
                                <Text
                                  component="span"
                                  size="9px"
                                  ml={4}
                                  style={{
                                    opacity: 0.7,
                                    fontWeight: 400,
                                  }}
                                >
                                  ({tema.reactivosCount})
                                </Text>
                              )}
                            </Button>
                          );
                        })}
                      </Group>
                    </Accordion.Panel>
                  </Accordion.Item>
                );
                })}
              </Accordion>
            </Card>
            )}
          </Stack>
        </Grid.Col>

        {/* Right Column - Configuration & Summary */}
        <Grid.Col span={6}>
          <Stack gap="xs" style={{ height: '100%' }}>
            {/* Configuration Card */}
            <Card
              padding="sm"
              style={{
                backgroundColor: colorScheme === 'dark'
                  ? 'rgba(30, 41, 59, 0.6)'
                  : '#fafafa',
                border: `1px solid ${colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(221, 216, 209, 0.8)'}`,
                borderRadius: '12px',
                flex: 1,
              }}
            >
              <Text size="sm" fw={600} mb="xs">Configuración</Text>

              <Stack gap="xs">
                <NumberInput
                  label="Número de Reactivos"
                  description="Entre 0 y 50 preguntas simples"
                  placeholder="Ej: 10"
                  value={numReactivos}
                  onChange={(val) => setNumReactivos(val as number | undefined)}
                  min={0}
                  max={50}
                  size="sm"
                  hideControls
                  styles={{
                    root: {
                      textAlign: 'left',
                    },
                    label: {
                      color: colorScheme === 'dark' ? '#e2e8f0' : '#2d2a26',
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 500,
                      fontSize: '14px',
                      textAlign: 'left',
                    },
                    description: {
                      color: colorScheme === 'dark' ? '#94a3b8' : '#5a5550',
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '12px',
                      textAlign: 'left',
                    },
                    input: {
                      backgroundColor: colorScheme === 'dark'
                        ? 'rgba(255, 255, 255, 0.08)'
                        : '#ffffff',
                      border: `1.5px solid ${colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(148, 163, 184, 0.4)'}`,
                      borderRadius: '12px',
                      fontSize: '15px',
                      fontWeight: 500,
                      color: colorScheme === 'dark' ? '#e2e8f0' : '#1e293b',
                      textAlign: 'left',
                      padding: '10px 14px',
                      height: '42px',
                    },
                  }}
                />

                <Divider 
                  size="xs" 
                  color={colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(221, 216, 209, 0.5)'} 
                  my="xs"
                />

                <NumberInput
                  label="Número de Casos de Estudio"
                  description="Entre 0 y 10 casos clínicos"
                  placeholder="Ej: 2"
                  value={numCasos}
                  onChange={(val) => setNumCasos(val as number | undefined)}
                  min={0}
                  max={10}
                  size="sm"
                  hideControls
                  styles={{
                    root: { textAlign: 'left' },
                    label: {
                      color: colorScheme === 'dark' ? '#e2e8f0' : '#2d2a26',
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 500,
                      fontSize: '14px',
                      textAlign: 'left',
                    },
                    description: {
                      color: colorScheme === 'dark' ? '#94a3b8' : '#5a5550',
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '12px',
                      textAlign: 'left',
                    },
                    input: {
                      backgroundColor: colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.08)' : '#ffffff',
                      border: `1.5px solid ${colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(148, 163, 184, 0.4)'}`,
                      borderRadius: '12px',
                      fontSize: '15px',
                      fontWeight: 500,
                      color: colorScheme === 'dark' ? '#e2e8f0' : '#1e293b',
                      textAlign: 'left',
                      padding: '10px 14px',
                      height: '42px',
                    },
                  }}
                />

                <Divider 
                  size="xs" 
                  color={colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(221, 216, 209, 0.5)'} 
                  my="xs"
                />

                {/* Selector de Dificultad */}
                <Box>
                  <Text
                    size="sm"
                    fw={500}
                    mb="xs"
                    style={{
                      color: colorScheme === 'dark' ? '#e2e8f0' : '#2d2a26',
                      fontFamily: 'Inter, sans-serif',
                      textAlign: 'left',
                    }}
                  >
                    Dificultad
                  </Text>
                  <Text
                    size="xs"
                    mb="sm"
                    style={{
                      color: colorScheme === 'dark' ? '#94a3b8' : '#5a5550',
                      fontFamily: 'Inter, sans-serif',
                      textAlign: 'left',
                    }}
                  >
                    Opcional - Filtra por nivel de dificultad
                  </Text>
                  <Group gap="xs">
                    <Button
                      variant={selectedDificultad === 1 ? "filled" : "default"}
                      color="gray"
                      size="xs"
                      radius="md"
                      onClick={() => setSelectedDificultad(selectedDificultad === 1 ? null : 1)}
                    >
                      Fácil
                    </Button>
                    <Button
                      variant={selectedDificultad === 2 ? "filled" : "default"}
                      color="gray"
                      size="xs"
                      radius="md"
                      onClick={() => setSelectedDificultad(selectedDificultad === 2 ? null : 2)}
                    >
                      Intermedio
                    </Button>
                    <Button
                      variant={selectedDificultad === 3 ? "filled" : "default"}
                      color="gray"
                      size="xs"
                      radius="md"
                      onClick={() => setSelectedDificultad(selectedDificultad === 3 ? null : 3)}
                    >
                      Difícil
                    </Button>
                  </Group>
                </Box>
              </Stack>
            </Card>

            {/* Summary Card */}
            <Card
              padding="sm"
              style={{
                backgroundColor: colorScheme === 'dark'
                  ? 'rgba(30, 41, 59, 0.6)'
                  : '#fafafa',
                border: `1px solid ${colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(221, 216, 209, 0.8)'}`,
                borderRadius: '12px',
              }}
            >
              <Text size="sm" fw={600} mb="xs">Resumen</Text>

              <Stack gap={4} style={{ textAlign: 'left' }}>
                <Group justify="space-between">
                  <Text size="xs">Temas:</Text>
                  <Text size="xs" fw={600}>{selectedTemas.length}</Text>
                </Group>

                <Group justify="space-between">
                  <Text size="xs">Reactivos:</Text>
                  <Text size="xs" fw={600}>{numReactivos || 0}</Text>
                </Group>

                <Group justify="space-between">
                  <Text size="xs">Casos de estudio:</Text>
                  <Text size="xs" fw={600}>{numCasos || 0}</Text>
                </Group>

                <Group justify="space-between">
                  <Text size="xs">Dificultad:</Text>
                  <Text size="xs" fw={600}>{selectedDificultad ? (selectedDificultad === 1 ? 'Fácil' : selectedDificultad === 2 ? 'Intermedio' : 'Difícil') : 'Todas'}</Text>
                </Group>

              </Stack>
            </Card>

            <Button
              fullWidth
              size="sm"
              radius="md"
              onClick={handleGenerarExamen}
              disabled={selectedTemas.length === 0 || ((numReactivos || 0) === 0 && (numCasos || 0) === 0)}
              styles={{
                root: {
                  backgroundColor:
                    (selectedTemas.length === 0 || (numReactivos || 0) === 0)
                      ? '#d4c4b0'
                      : '#8B6F47',
                  color: 'white',
                  '&:hover': {
                    backgroundColor:
                      (selectedTemas.length === 0 || (numReactivos || 0) === 0)
                        ? '#d4c4b0'
                        : '#735a38',
                  },
                },
              }}
            >
              Generar Examen
            </Button>
          </Stack>
        </Grid.Col>
      </Grid>
      </Box>
    </>
  );
};

export default ExamenFiltrosPage;
